import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DevToolsService } from '../dev-tools.service';
import { DevToolsEvent } from '../dev-tools.types';
import { JsonTreeComponent } from './json-tree.component';

interface InjectEventView {
  id: string;
  timestamp: number;
  stage: string;
  page: string;
  taskId: string;
  demarcheRef: string;
  keys: string[];
  payload: unknown;
}

/**
 * Onglet "Injection" — trace le cycle de vie des variables injectées dans les
 * formulaires via `gnspdTreatmentInjectedVariable` (canal certification handler) :
 *   VariablesParsed   → lues depuis task.parameters (demarches-form.service)
 *   VariablesLoaded   → reçues par le composant formulaire (navState/session)
 *   VariablesServed   → fusionnées dans la submission de RENDU (lisibles par
 *                       les composants Formio via data.<clé>)
 *   VariablesApplied  → injectées dans formData.submission.data à la soumission
 */
@Component({
  selector: 'dev-form-inject-tab',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, JsonTreeComponent],
  template: `
    <div class="tw-flex tw-flex-col tw-h-full tw-text-gray-100">
      <!-- Toolbar -->
      <div class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-border-b tw-border-gray-700/60 tw-bg-gray-900/60 tw-shrink-0">
        <div class="tw-relative tw-flex-1">
          <i class="ph ph-magnifying-glass tw-absolute tw-left-2 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-500 tw-text-[12px]"></i>
          <input
            type="text"
            [value]="search()"
            (input)="onSearch($event)"
            placeholder="Filtrer par clé, task, référence…"
            class="tw-w-full tw-bg-gray-800/70 tw-border tw-border-gray-700 tw-rounded tw-pl-7 tw-pr-2 tw-py-1 tw-text-[11px] tw-text-gray-200 placeholder:tw-text-gray-500 focus:tw-outline-none focus:tw-border-success-500"
          />
        </div>
        <span class="tw-text-[10px] tw-text-gray-500">{{ filtered().length }} injection(s)</span>
      </div>

      <!-- Timeline -->
      <div class="tw-flex-1 tw-overflow-auto">
        <div class="tw-divide-y tw-divide-gray-800">
          @for (ev of filtered(); track ev.id) {
            <div class="tw-px-2 tw-py-1">
              <button
                type="button"
                class="tw-w-full tw-flex tw-items-center tw-gap-2 tw-text-left hover:tw-bg-gray-800/40 tw-rounded tw-px-1 tw-py-0.5"
                (click)="toggle(ev.id)"
              >
                <i class="ph tw-text-[11px] tw-text-gray-400"
                   [ngClass]="isOpen(ev.id) ? 'ph-caret-down' : 'ph-caret-right'"></i>
                <span class="tw-text-[9px] tw-w-[64px] tw-text-center tw-px-1 tw-py-0.5 tw-rounded tw-font-mono tw-font-bold tw-shrink-0"
                      [ngClass]="stageClass(ev.stage)">{{ stageLabel(ev.stage) }}</span>
                <span class="tw-text-[10px] tw-text-gray-300 tw-font-mono tw-truncate">{{ ev.keys.join(', ') || '—' }}</span>
                @if (ev.demarcheRef) {
                  <span class="tw-text-[9px] tw-text-info-500 tw-font-mono tw-shrink-0">{{ ev.demarcheRef }}</span>
                }
                <span class="tw-ml-auto tw-text-[9px] tw-text-gray-500 tw-font-mono tw-shrink-0">{{ ev.page }}</span>
                <span class="tw-text-[9px] tw-text-gray-500 tw-font-mono tw-shrink-0">{{ timeLabel(ev.timestamp) }}</span>
              </button>
              @if (isOpen(ev.id)) {
                <div class="tw-pl-6 tw-pt-1 tw-pb-1 tw-overflow-x-auto">
                  <dev-json-tree [value]="ev.payload" [search]="search()" [defaultExpanded]="true" />
                </div>
              }
            </div>
          } @empty {
            <div class="tw-text-[11px] tw-text-gray-500 tw-italic tw-px-3 tw-py-4 tw-text-center">
              Aucune injection capturée. Ouvre une tâche dont
              <code class="tw-text-gray-400">task.parameters.gnspdTreatmentInjectedVariable</code>
              est renseigné : le parse, la réception par le formulaire et l'application
              à la soumission apparaîtront ici.
            </div>
          }
        </div>
      </div>
    </div>
  `,
})
export class DevToolsFormInjectTabComponent {
  private readonly devTools = inject(DevToolsService);

  readonly search = signal<string>('');
  private readonly openIds = signal<Set<string>>(new Set());

  readonly all = computed<InjectEventView[]>(() =>
    this.devTools
      .events()
      .filter((e) => e.source === 'form-inject')
      .map((e) => this.toView(e))
      .reverse()
  );

  readonly filtered = computed<InjectEventView[]>(() => {
    const term = this.search().trim().toLowerCase();
    if (!term) return this.all();
    return this.all().filter((e) =>
      [e.stage, e.page, e.taskId, e.demarcheRef, ...e.keys]
        .join(' ')
        .toLowerCase()
        .includes(term)
    );
  });

  private toView(e: DevToolsEvent): InjectEventView {
    const p = (e.payload ?? {}) as Record<string, unknown>;
    const variables = p['variables'];
    return {
      id: e.id,
      timestamp: e.timestamp,
      stage: e.type,
      page: typeof p['page'] === 'string' ? (p['page'] as string) : '',
      taskId: typeof p['taskId'] === 'string' ? (p['taskId'] as string) : '',
      demarcheRef: e.demarcheRef ?? '',
      keys:
        variables && typeof variables === 'object'
          ? Object.keys(variables as Record<string, unknown>)
          : [],
      payload: e.payload,
    };
  }

  onSearch(ev: Event): void {
    this.search.set((ev.target as HTMLInputElement).value);
  }

  toggle(id: string): void {
    const next = new Set(this.openIds());
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this.openIds.set(next);
  }

  isOpen(id: string): boolean {
    return this.openIds().has(id);
  }

  timeLabel(ts: number): string {
    try {
      return new Date(ts).toLocaleTimeString();
    } catch {
      return String(ts);
    }
  }

  stageLabel(stage: string): string {
    switch (stage) {
      case 'VariablesParsed':
        return 'PARSED';
      case 'VariablesLoaded':
        return 'LOADED';
      case 'VariablesServed':
        return 'SERVED';
      case 'VariablesApplied':
        return 'APPLIED';
      default:
        return stage.toUpperCase().slice(0, 8);
    }
  }

  stageClass(stage: string): string {
    switch (stage) {
      case 'VariablesParsed':
        return 'tw-bg-gray-700/40 tw-text-gray-300';
      case 'VariablesLoaded':
        return 'tw-bg-warning-500/15 tw-text-warning-500';
      case 'VariablesServed':
        return 'tw-bg-info-500/15 tw-text-info-500';
      case 'VariablesApplied':
        return 'tw-bg-success-500/15 tw-text-success-500';
      default:
        return 'tw-bg-gray-700/40 tw-text-gray-300';
    }
  }
}
